// src/components/Invoices/InvoiceTable.jsx
import React, { useState } from "react";
import { Table, Row, Col, Spin } from "antd";
import axios from "axios";
import PdfViewer from "../Actions/PdfCanvasViewer";

// Kleine Hilfsfunktion für Beträge
const fmt = (v, digits = 2) =>
    v == null || v === "" ? "-" : Number(v).toFixed(digits);

// Positionen/Artikel-Tabelle
function InvoiceLinesTable({ lines }) {
    const cols = [
        { title: "Artikel (raw)", dataIndex: "product_name_raw", key: "name", width: 320 },
        { title: "Menge", dataIndex: "quantity", key: "qty", align: "right", render: (v) => fmt(v, 3) },
        { title: "Einheit", dataIndex: "uom_raw", key: "uom", width: 80 },
        { title: "EP", dataIndex: "unit_price", key: "unit_price", align: "right", render: (v) => fmt(v, 4) },
        { title: "Netto", dataIndex: "line_net", key: "line_net", align: "right", render: (v) => fmt(v, 2) },
        { title: "MwSt %", dataIndex: "vat_rate", key: "vat_rate", align: "right", render: (v) => fmt(v, 2) },
        { title: "Währung", dataIndex: "currency", key: "currency", width: 80 },
    ];
    return (
        <Table
            rowKey={(r) => r.id ?? `${r.product_name_raw}-${r.line_net}-${r.quantity}`}
            columns={cols}
            dataSource={lines || []}
            pagination={false}
            size="small"
        />
    );
}

const InvoiceTable = ({ invoices }) => {
    // Cache für expandierte Details: { [invoiceId|id]: { loading, invoice, lines } }
    const [expanded, setExpanded] = useState({});

    async function loadDetail(idOrKey) {
        setExpanded((s) => ({ ...s, [idOrKey]: { ...(s[idOrKey] || {}), loading: true } }));
        try {
            const res = await axios.get(`/api/invoices/${idOrKey}`);
            setExpanded((s) => ({ ...s, [idOrKey]: { ...res.data, loading: false } }));
        } catch (e) {
            console.error("Detail-Laden fehlgeschlagen:", e?.response?.status, e?.message);
            setExpanded((s) => ({ ...s, [idOrKey]: { loading: false, invoice: null, lines: [] } }));
        }
    }

    const columns = [
        { title: "Rechnungs-Nr.", dataIndex: "invoice_id", key: "invoice_id" },
        { title: "Lieferant", dataIndex: "supplier_name", key: "supplier_name" },
        { title: "Rechnungsdatum", dataIndex: "invoice_date", key: "invoice_date" },
        { title: "Netto (€)", dataIndex: "net_amount", key: "net_amount", align: "right", render: (v) => fmt(v) },
        { title: "MwSt (€)", dataIndex: "total_tax_amount", key: "total_tax_amount", align: "right", render: (v) => fmt(v) },
        { title: "Betrag (€)", dataIndex: "total_amount", key: "total_amount", align: "right", render: (v) => fmt(v) },
        { title: "Währung", dataIndex: "currency", key: "currency", width: 80 },
    ];

    return (
        <Table
            columns={columns}
            dataSource={invoices}
            // Nutze DB-ID wenn vorhanden, sonst invoice_id
            rowKey={(r) => r.id ?? r.invoice_id}
            expandable={{
                expandedRowRender: (record) => {
                    const key = record.id ?? record.invoice_id;
                    const data = expanded[key] || {};
                    if (data.loading) return <Spin />;

                    // PDF-URL: nutze den Static-Serve Pfad /uploads (keine harte Host-Angabe!)
                    // Falls pdf_path bereits "uploads/..." enthält, entferne das doppelte.
                    const pdfRel =
                        data?.invoice?.pdf_path ||
                        record.pdf_path ||
                        ""; // fallback, falls Detail noch nicht geladen
                    const pdfUrl = pdfRel
                        ? pdfRel.startsWith("/uploads/")
                            ? pdfRel
                            : `/uploads/${pdfRel.replace(/^uploads\//, "")}`
                        : null;

                    return (
                        <div style={{ padding: 8 }}>
                            <Row gutter={12}>
                                <Col span={14}>
                                    <InvoiceLinesTable lines={data.lines || []} />
                                </Col>
                                <Col span={10}>
                                    {pdfUrl ? (
                                        <div style={{ border: "1px solid #eee", borderRadius: 6, padding: 4 }}>
                                            <PdfViewer pdfUrl={pdfUrl} />
                                        </div>
                                    ) : (
                                        <div style={{ padding: 8 }}>Keine PDF verfügbar</div>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    );
                },
                onExpand: (expandedRow, record) => {
                    if (expandedRow) {
                        const key = record.id ?? record.invoice_id;
                        if (!expanded[key]) loadDetail(key);
                    }
                },
                // Zeile expandierbar lassen – PDF kommt aus Detail; wenn du hard verhindern willst:
                // rowExpandable: (record) => !!(record.pdf_path),
            }}
            pagination={false}
        />
    );
};

export default InvoiceTable;
